import { supabase } from './supabase'
import { countryNameToIso2 } from '../lib/cds/countries'

// Z57.3: this used to create its own second createClient() instance (same URL/anon key as
// api/supabase.ts's shared client) -- two GoTrueClient instances persisting sessions under the
// same default storage key is exactly the "Multiple GoTrueClient instances detected" warning,
// and can leave one instance holding a stale/different session than the other, causing spurious
// 403s on RLS-gated tables (auth.uid() resolving against whichever client's token happened to
// be current) that have nothing to do with whatever page triggered this file to load.

// Fingerprint of a template's source layout, used to auto-pick the right
// template when a customer has more than one format (different suppliers
// sending on their behalf, a redesigned invoice, a separate product line).
// Deliberately generic (labels/positions derived from whatever this
// template's own field_mappings turned out to be) rather than anything
// per-supplier-hardcoded.
export interface StructureSignature {
  top_left_label?: string
  invoice_no_label?: string
  item_header_row?: number
  item_columns?: string[]
  header_field_labels?: Record<string, string>
}

// Batch Q: how a header field's value is located, so a template survives
// row-position drift (an extra blank row inserted above the invoice, a
// redesigned header) instead of reading whatever now sits at a hardcoded
// {row, col}. `row`/`col` are always kept as a fallback so a pre-Batch-Q
// template (no strategy at all) keeps working exactly as before.
export interface HeaderFieldSpec {
  strategy?: 'anchor' | 'derive-from-items' | 'fixed'
  // anchor: search near anchor_row_hint for anchor_label's text, then read
  // the value at that cell + value_offset. Falls back to row/col if the
  // label can't be found (e.g. a totally different-looking file).
  anchor_label?: string
  anchor_row_hint?: number
  value_offset?: { row: number; col: number }
  // derive-from-items: no cell is read at all -- the value is computed from
  // the already-parsed items array, so it's immune to a "totals row" that
  // moves depending on how many line items there are. derive_from is
  // 'items.<itemField>', e.g. 'items.amount'.
  derive_from?: string
  derive_op?: 'sum' | 'count'
  // fixed (or no strategy): read row/col directly, no search.
  row?: number
  col?: number
  strip_before?: string
  strip_after?: string
  trim?: boolean
}

// Batch Q: the item table's header row (and each column's position within
// it) can also be relocated by label text, so an extra/removed row above the
// table doesn't misalign start_row against a hardcoded offset. start_row
// (and each column's plain col number) remain the fallback.
export interface ItemTableSpec {
  header_row_hint?: number
  header_labels?: string[]
  data_start_row_offset?: number
  start_row?: number
  end_condition?: string
  columns?: Record<string, number | { col: number; header_label?: string; strip_before?: string; strip_after?: string; trim?: boolean }>
}

// Z42.2.2: which physical sheet (multi_sheet) or file (two_files) staff
// explicitly assigned as the Invoice vs the Packing List when the template
// was created -- lets replay re-locate the right sheet by name on a future
// upload before falling back to the generic role-detection heuristic (see
// readNamedSheets in parseCommercialInvoiceFile.ts).
export interface SheetAssignment {
  sheetName?: string
}
export interface SheetAssignments {
  invoice?: SheetAssignment
  pl?: SheetAssignment
}

// Z42.2.2: a lightweight, staff-entered alternative to structure_signature's
// content fingerprint -- literal shipper text expected somewhere near the top
// of the file, and/or a substring expected in the uploaded filename(s).
// Checked first (see matchTemplateBySignature) because it's simpler and more
// direct than structure-signature scoring; falls through to that scoring
// when it doesn't resolve to exactly one template.
export interface TemplateSignature {
  shipperText?: string
  fileNamePattern?: string
}

export interface InvoiceTemplate {
  id?: string
  customer_id: string
  // Staff-facing label so a customer's N templates are distinguishable in
  // the Templates list, e.g. "箱单发票 KMS v1", "海运分单 standard".
  name: string
  field_mappings: any
  structure_signature?: StructureSignature
  file_type: 'pdf' | 'xlsx' | 'xls' | 'any'
  // Z42.2.2: how the sample file(s) this template was built from were laid
  // out -- null/undefined means a pre-Z42.2.2 template, replayed the same
  // way it always was (auto-detecting multi-sheet reader).
  structure_type?: 'single_sheet' | 'multi_sheet' | 'two_files' | null
  sheet_assignments?: SheetAssignments
  signature?: TemplateSignature
  // Deprecated: pre-Batch-P keyword-detection fields. No longer written by new
  // templates -- kept nullable so old rows (re-linked or orphaned by the Batch P
  // migration) still round-trip through getAllTemplates/updateTemplate.
  agent_name?: string | null
  detection_keywords?: string[] | null
  is_orphaned?: boolean
  sample_notes?: string
  usage_count?: number
  last_used_at?: string | null
  created_by?: string
  created_at?: string
  updated_at?: string
}

// Fetch all templates
export async function getAllTemplates(): Promise<InvoiceTemplate[]> {
  const { data, error } = await supabase
    .from('invoice_templates')
    .select('*')
    .order('usage_count', { ascending: false })

  if (error) throw error
  return data || []
}

export async function getTemplateById(id: string): Promise<InvoiceTemplate | null> {
  const { data, error } = await supabase
    .from('invoice_templates')
    .select('*')
    .eq('id', id)
    .maybeSingle()

  if (error) {
    console.error('[getTemplateById] Error:', error)
    return null
  }
  return data || null
}

// All templates linked to a customer -- a customer can have more than one
// format, so callers must score/pick among these (see matchTemplates)
// rather than assume the first result is the right one.
export async function getTemplatesForCustomer(
  customerId: string,
  fileType: 'pdf' | 'xlsx' | 'xls'
): Promise<InvoiceTemplate[]> {
  const { data, error } = await supabase
    .from('invoice_templates')
    .select('*')
    .eq('customer_id', customerId)
    .in('file_type', [fileType, 'any'])

  if (error) {
    console.error('[getTemplatesForCustomer] Error:', error)
    return []
  }
  return data || []
}

// Create new template
export async function createTemplate(template: InvoiceTemplate): Promise<InvoiceTemplate> {
  const { data, error } = await supabase
    .from('invoice_templates')
    .insert([template])
    .select()
    .single()

  if (error) throw error
  return data
}

// Update template
export async function updateTemplate(id: string, updates: Partial<InvoiceTemplate>): Promise<InvoiceTemplate> {
  const { data, error } = await supabase
    .from('invoice_templates')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return data
}

// Delete template
export async function deleteTemplate(id: string): Promise<void> {
  const { error } = await supabase
    .from('invoice_templates')
    .delete()
    .eq('id', id)

  if (error) throw error
}

// Records that a template was actually applied to parse an upload -- drives
// the Templates list's last-used column. Called on save, whether or not the
// reviewer edited anything afterward (usage is about the parse, not the edit).
export async function touchTemplateUsage(id: string): Promise<void> {
  const { data } = await supabase.from('invoice_templates').select('usage_count').eq('id', id).maybeSingle()
  const { error } = await supabase
    .from('invoice_templates')
    .update({ usage_count: (data?.usage_count || 0) + 1, last_used_at: new Date().toISOString() })
    .eq('id', id)
  if (error) console.error('[touchTemplateUsage] Error:', error)
}

// Some mapped cells carry a fixed label alongside the value (e.g.
// "INVOICE NO. : 2026-MF062603") -- strips it so replay recovers the clean
// value instead of the whole cell string. Prefix/suffix matching is
// case-insensitive; trim defaults to true.
export function applyStrip(raw: unknown, spec: { strip_before?: string; strip_after?: string; trim?: boolean }): unknown {
  if (typeof raw !== 'string') return raw
  let value = raw
  if (spec.strip_before && value.toLowerCase().startsWith(spec.strip_before.toLowerCase())) {
    value = value.slice(spec.strip_before.length)
  }
  if (spec.strip_after && value.toLowerCase().endsWith(spec.strip_after.toLowerCase())) {
    value = value.slice(0, value.length - spec.strip_after.length)
  }
  if (spec.trim !== false) value = value.trim()
  return value
}

// Derives a structure fingerprint from this template's own field_mappings and
// the rows it was built from -- captured once at template save time, then
// compared against every future upload from the same customer to pick which
// of that customer's (possibly several) templates actually matches.
export function computeStructureSignature(rows: any[][], fieldMappings: any): StructureSignature {
  const mappings = fieldMappings || {}
  const signature: StructureSignature = {}

  const topLeft = String(rows[0]?.[0] ?? '').trim()
  if (topLeft) signature.top_left_label = topLeft

  const invoiceNoSpec = mappings.invoice_no
  if (invoiceNoSpec?.strip_before) {
    signature.invoice_no_label = String(invoiceNoSpec.strip_before).trim()
  }

  const itemTable = mappings.item_table
  if (itemTable && typeof itemTable.start_row === 'number' && itemTable.start_row > 0) {
    const headerRow = itemTable.start_row - 1
    const colIndices = Object.values(itemTable.columns || {})
      .map((c: any) => (typeof c === 'object' && c !== null ? c.col : c))
      .filter((c: any) => typeof c === 'number')
      .sort((a: number, b: number) => a - b)
    const labels = colIndices
      .map(c => String(rows[headerRow]?.[c] ?? '').trim())
      .filter(Boolean)
    if (labels.length > 0) {
      signature.item_header_row = headerRow
      signature.item_columns = labels
    }
  }

  const headerFieldLabels: Record<string, string> = {}
  for (const [field, spec] of Object.entries(mappings)) {
    if (field === 'item_table') continue
    const specObj = spec as any
    if (!specObj || typeof specObj.row !== 'number' || typeof specObj.col !== 'number') continue
    if (specObj.strip_before) {
      headerFieldLabels[field] = String(specObj.strip_before).trim()
    } else {
      // Common layout even without cell-level label noise: a short label
      // sitting directly above the value (e.g. "FROM:" above the shipper name).
      const above = String(rows[specObj.row - 1]?.[specObj.col] ?? '').trim()
      if (above && above.length <= 40) headerFieldLabels[field] = above
    }
  }
  if (Object.keys(headerFieldLabels).length > 0) signature.header_field_labels = headerFieldLabels

  return signature
}

// Scores how well a template's captured signature matches a freshly-uploaded
// file's actual layout. Each applicable signal (file type, top-left label,
// invoice-no label present anywhere near the top, item-header-row labels,
// other header field labels) contributes at most 1 point; a signature with
// no data for a signal simply doesn't count it, in either direction.
export function scoreTemplateMatch(
  signature: StructureSignature,
  rows: any[][],
  fileType: string,
  templateFileType: string
): { score: number; possible: number } {
  let score = 0
  let possible = 0

  possible++
  if (templateFileType === 'any' || templateFileType === fileType) score++

  if (signature.top_left_label) {
    possible++
    if (String(rows[0]?.[0] ?? '').trim() === signature.top_left_label) score++
  }

  if (signature.invoice_no_label) {
    possible++
    const needle = signature.invoice_no_label.toLowerCase()
    const found = rows.slice(0, 30).some(row => row?.some(cell => typeof cell === 'string' && cell.toLowerCase().includes(needle)))
    if (found) score++
  }

  if (signature.item_header_row != null && signature.item_columns && signature.item_columns.length > 0) {
    possible++
    const rowText = (rows[signature.item_header_row] || []).map(c => String(c ?? '').toLowerCase())
    const matched = signature.item_columns.filter(label => rowText.some(cell => cell.includes(label.toLowerCase()))).length
    if (matched >= Math.ceil(signature.item_columns.length / 2)) score++
  }

  if (signature.header_field_labels && Object.keys(signature.header_field_labels).length > 0) {
    possible++
    const labels = Object.values(signature.header_field_labels)
    const flatText = rows.slice(0, 30).flatMap(row => (row || []).map((c: any) => String(c ?? '').toLowerCase()))
    const matched = labels.filter(label => flatText.some(cell => cell.includes(label.toLowerCase()))).length
    if (matched >= Math.ceil(labels.length / 2)) score++
  }

  return { score, possible }
}

export interface TemplateMatchResult {
  // Exactly one confident match -- use it.
  matched: InvoiceTemplate | null
  // 2+ candidates scored above threshold within 1 point of each other --
  // genuinely ambiguous, caller must ask staff to pick (or fall back to AI).
  ambiguous: InvoiceTemplate[] | null
}

// Scores all of a customer's templates against the freshly-uploaded rows and
// decides which of three outcomes applies: exactly one confident match, an
// ambiguous tie needing a staff pick, or no match (both fields null --
// caller falls through to AI). Threshold is 4 matching signals, capped at
// whatever each signature actually captured -- a sparse signature has to
// match on everything it has, not get an easier bar than a fuller one.
export function matchTemplates(templates: InvoiceTemplate[], rows: any[][], fileType: string): TemplateMatchResult {
  if (templates.length === 0) return { matched: null, ambiguous: null }
  if (templates.length === 1) {
    // Nothing to disambiguate -- use it even with a thin/empty signature.
    // Covers templates auto-created by the routine upload flow, which has no
    // structure_signature computed (it never has the rows[][] on hand for
    // it) -- those rely entirely on this single-template fallback, since an
    // empty signature would otherwise match indiscriminately (see the
    // `possible < 2` guard below) once a second template exists.
    return { matched: templates[0], ambiguous: null }
  }

  const passed = templates
    .map(template => ({ template, ...scoreTemplateMatch(template.structure_signature || {}, rows, fileType, template.file_type) }))
    // With multiple candidates, the trivial file-type-only signal can't
    // disambiguate between them -- require at least one real signature signal.
    .filter(s => s.possible >= 2)
    .filter(s => s.score >= Math.min(4, s.possible))
    .sort((a, b) => b.score - a.score)

  if (passed.length === 0) return { matched: null, ambiguous: null }
  if (passed.length === 1) return { matched: passed[0].template, ambiguous: null }

  const topScore = passed[0].score
  const contenders = passed.filter(s => topScore - s.score <= 1)
  if (contenders.length > 1) return { matched: null, ambiguous: contenders.map(c => c.template) }
  return { matched: passed[0].template, ambiguous: null }
}

// Z42.2.2: simpler, staff-authored alternative to scoreTemplateMatch's
// content fingerprint -- a direct filename-substring or shipper-text check.
// Only returns a match when it resolves to exactly ONE template (same
// "don't guess when it's ambiguous" rule as matchTemplates); two templates
// sharing a signature, or no signature at all, falls through to the
// structure-signature scoring in matchTemplates instead.
export function matchTemplateBySignature(templates: InvoiceTemplate[], rows: any[][], fileNames: string[]): InvoiceTemplate | null {
  const lowerFileNames = fileNames.map(name => name.toLowerCase())
  const topRows = rows.slice(0, 10)
  const matches = templates.filter(template => {
    const sig = template.signature
    if (!sig) return false
    if (sig.fileNamePattern?.trim()) {
      const pattern = sig.fileNamePattern.trim().toLowerCase()
      if (lowerFileNames.some(name => name.includes(pattern))) return true
    }
    if (sig.shipperText?.trim()) {
      const needle = sig.shipperText.trim().toLowerCase()
      if (topRows.some(row => row?.some(cell => typeof cell === 'string' && cell.toLowerCase().includes(needle)))) return true
    }
    return false
  })
  return matches.length === 1 ? matches[0] : null
}

function rowRange(rows: any[][], hint: number | undefined, window: number): number[] {
  if (typeof hint !== 'number') return rows.map((_, i) => i)
  const out: number[] = []
  for (let r = Math.max(0, hint - window); r <= Math.min(rows.length - 1, hint + window); r++) out.push(r)
  return out
}

// Finds the first cell within +/-3 rows of hint (or anywhere, if there's no
// hint) whose text contains label -- tolerant of the label having drifted a
// few rows from where the template last saw it (e.g. a blank row inserted
// above it), without matching some unrelated occurrence of the same text far
// away in a long sheet.
function findAnchorCell(rows: any[][], label: string, hint?: number): { row: number; col: number } | null {
  const needle = label.trim().toLowerCase()
  if (!needle) return null
  const range = rowRange(rows, hint, 3)
  // Exact match first -- a substring-only search would misattribute e.g.
  // "NAME:" to "NAME/COMPANY:" sitting in the same row (a different field's
  // label that happens to contain this one as a substring). Substring stays
  // as the fallback tier for combined label+value cells, where the label is
  // deliberately only a prefix of the cell's full text.
  for (const r of range) {
    const row = rows[r]
    if (!row) continue
    for (let c = 0; c < row.length; c++) {
      const cell = row[c]
      if (typeof cell === 'string' && cell.trim().toLowerCase() === needle) return { row: r, col: c }
    }
  }
  for (const r of range) {
    const row = rows[r]
    if (!row) continue
    for (let c = 0; c < row.length; c++) {
      const cell = row[c]
      if (typeof cell === 'string' && cell.trim().toLowerCase().includes(needle)) return { row: r, col: c }
    }
  }
  return null
}

// Same idea as findAnchorCell, but for relocating the item table's header
// row: matches on labels[0] (the table's other columns are then found within
// whichever row this returns, via findColumnByLabel).
function findItemHeaderRow(rows: any[][], labels: string[], hint?: number): number | null {
  const primary = labels[0]
  if (!primary) return null
  const anchor = findAnchorCell(rows, primary, hint)
  return anchor ? anchor.row : null
}

function findColumnByLabel(headerRow: any[] | undefined, label: string): number | null {
  const needle = label.trim().toLowerCase()
  if (!needle || !headerRow) return null
  // Exact match first for the same reason as findAnchorCell -- e.g. "Value
  // (USD)" is a substring of "Unit Value (USD)" in the very same header row,
  // and a substring-only search picks whichever column comes first,
  // regardless of which one is actually correct.
  for (let c = 0; c < headerRow.length; c++) {
    const cell = headerRow[c]
    if (typeof cell === 'string' && cell.trim().toLowerCase() === needle) return c
  }
  for (let c = 0; c < headerRow.length; c++) {
    const cell = headerRow[c]
    if (typeof cell === 'string' && cell.trim().toLowerCase().includes(needle)) return c
  }
  return null
}

// Batch Q totals: 'sum' treats a missing/null item value as 0 EXCEPT when no
// item has the field at all, in which case the aggregate is genuinely
// unknown (null) rather than misleadingly 0 -- matches the pre-Batch-Q
// total_cbm behavior, generalized to every derive-from-items field.
//
// U2: summing floats in JS accumulates binary-representation drift (e.g.
// 15 items' amounts summing to 752.6000000000001, not 752.6) -- rounded to
// 3dp, matching the precision already used for weights/CBM elsewhere in
// this codebase (see GROSS_WEIGHT_EPSILON in runVerification.ts and the
// .toFixed(3) displays throughout). 3dp is enough for money fields too --
// a JS number doesn't retain trailing zeros, so 752.6 displays as "752.6"
// either way, not "752.600".
function deriveFromItems(items: any[], itemField: string, op: 'sum' | 'count'): number | null {
  if (op === 'count') return items.filter(item => item[itemField] != null).length
  const present = items.filter(item => item[itemField] != null)
  if (present.length === 0) return null
  const sum = present.reduce((total, item) => total + (Number(item[itemField]) || 0), 0)
  return Math.round(sum * 1000) / 1000
}

// Header fields with no explicit field_mappings entry (every pre-Batch-Q
// template, since the wizard never exposed these 4) still get a total --
// same formula as the old unconditional block below, just applied as the
// default instead of unconditionally. A confirmed strategy: 'anchor' |
// 'fixed' entry (staff explicitly picked a cell) takes priority.
const DEFAULT_TOTAL_ITEM_FIELDS: Record<string, string> = {
  total_amount: 'amount',
  gross_weight_kg: 'gross_weight',
  net_weight_kg: 'net_weight',
  packages: 'ctns',
  total_cbm: 'cbm',
}

// Applies a customer's cached field_mappings (row/col positions the AI reported
// on an earlier upload -- see FieldMappings in claudeInvoiceParser.ts) to a
// freshly-read rows[][] grid. Deterministic, no AI call.
export function parseUsingTemplate(rows: any[][], template: InvoiceTemplate): any {
  const mappings = template.field_mappings || {}
  const result: any = { header: {}, items: [], confidence: 0.95, raw_header_cells: {} }

  try {
    for (const [field, spec] of Object.entries(mappings)) {
      if (field === 'item_table') continue
      const specObj = spec as HeaderFieldSpec
      if (!specObj || specObj.strategy === 'derive-from-items') continue

      let cellRow: number | undefined
      let cellCol: number | undefined
      if (specObj.strategy === 'anchor' && specObj.anchor_label) {
        const anchor = findAnchorCell(rows, specObj.anchor_label, specObj.anchor_row_hint)
        if (anchor) {
          const offset = specObj.value_offset || { row: 0, col: 0 }
          cellRow = anchor.row + (offset.row || 0)
          cellCol = anchor.col + (offset.col || 0)
        }
      }
      if (cellRow === undefined && typeof specObj.row === 'number' && typeof specObj.col === 'number') {
        cellRow = specObj.row
        cellCol = specObj.col
      }

      if (cellRow !== undefined && cellCol !== undefined) {
        const raw = rows[cellRow]?.[cellCol]
        result.raw_header_cells[field] = raw ?? null
        result.header[field] = applyStrip(raw, specObj) ?? null
      }
    }

    const itemTable = mappings.item_table as ItemTableSpec | undefined

    if (itemTable) {
      let startRow = itemTable.start_row ?? 0
      let columns = itemTable.columns || {}

      if (itemTable.header_labels && itemTable.header_labels.length > 0) {
        const headerRow = findItemHeaderRow(rows, itemTable.header_labels, itemTable.header_row_hint)
        if (headerRow != null) {
          startRow = headerRow + (itemTable.data_start_row_offset ?? 1)
          const relocated: Record<string, any> = {}
          for (const [field, colSpec] of Object.entries(columns)) {
            const isObj = typeof colSpec === 'object' && colSpec !== null
            const headerLabel = isObj ? (colSpec as any).header_label : undefined
            const fallbackCol = isObj ? (colSpec as any).col : colSpec
            const foundCol = headerLabel ? findColumnByLabel(rows[headerRow], headerLabel) : null
            const col = foundCol ?? fallbackCol
            relocated[field] = isObj ? { ...(colSpec as any), col } : col
          }
          columns = relocated
        }
        // headerRow not found: keep the legacy start_row/columns as-is --
        // same fallback behavior as a header field whose anchor_label search
        // comes up empty.
      }

      const endColMatch = itemTable.end_condition?.match(/^empty_col_(\d+)$/)
      const endCol = endColMatch ? parseInt(endColMatch[1], 10) : null
      let lineNo = 1

      for (let i = startRow; i < rows.length; i++) {
        const row = rows[i]
        if (!row) break
        if (endCol !== null && !row[endCol]) break

        const item: any = { line_no: lineNo }

        for (const [field, colSpec] of Object.entries(columns)) {
          const isObj = typeof colSpec === 'object' && colSpec !== null
          const colIndex = isObj ? (colSpec as any).col : colSpec
          const raw = row[colIndex as any]
          const value = isObj ? applyStrip(raw, colSpec as any) : raw
          if (value !== undefined && value !== null && value !== '') {
            if (field === 'pcs' || field === 'ctns') {
              item[field] = parseInt(String(value)) || 0
            } else if (field === 'gross_weight' || field === 'net_weight' || field === 'unit_value' || field === 'amount' || field === 'cbm') {
              // Weights/values are frequently fractional (e.g. 361.7 kg) --
              // parseInt here would silently truncate every line's decimals,
              // which matters for customs weight/value accuracy.
              item[field] = parseFloat(String(value)) || 0
            } else if (field === 'country_of_origin') {
              // Template-replayed cells carry whatever raw text the sheet has
              // ("China", "United Kingdom") -- the AI path already normalizes
              // to ISO-2, so match that here instead of writing free text.
              item[field] = countryNameToIso2(String(value)) || value
            } else {
              item[field] = value
            }
          } else {
            item[field] = field.includes('_weight') || field === 'pcs' || field === 'ctns' ? 0 : null
          }
        }

        if (item.goods_description || item.hs_code) {
          result.items.push(item)
          lineNo++
        }
      }
    }

    // Totals: honor an explicit anchor/fixed pick (already populated above)
    // for the field; otherwise derive from items -- either via an explicit
    // derive-from-items entry's derive_from/derive_op, or (pre-Batch-Q
    // templates with no entry for this field at all) the same formula the
    // old unconditional block used.
    for (const [field, defaultItemField] of Object.entries(DEFAULT_TOTAL_ITEM_FIELDS)) {
      const spec = mappings[field] as HeaderFieldSpec | undefined
      const pointsAtACell = spec && (spec.strategy === 'anchor' || spec.strategy === 'fixed'
        || (!spec.strategy && typeof spec.row === 'number' && typeof spec.col === 'number'))
      if (pointsAtACell) continue
      const itemField = spec?.derive_from ? spec.derive_from.replace(/^items\./, '') : defaultItemField
      result.header[field] = deriveFromItems(result.items, itemField, spec?.derive_op || 'sum')
    }

    // Template-replayed header cells carry whatever raw text the sheet has
    // ("China", "United Kingdom") -- normalize to ISO-2 the same way the AI
    // path already does, before falling back to the default.
    result.header.country_of_origin = countryNameToIso2(result.header.country_of_origin) || result.header.country_of_origin || 'CN'
    result.header.destination_country = countryNameToIso2(result.header.destination_country) || result.header.destination_country || 'GB'
    result.header.currency = result.header.currency || 'USD'
    result.header.vat_deferred = result.header.vat_deferred || false
    result.header.use_cyuk_dan = result.header.use_cyuk_dan || false

    return result
  } catch (err) {
    console.error('[parseUsingTemplate] Error:', err)
    return null
  }
}

const DERIVE_FROM_ITEMS_FIELDS: Record<string, string> = DEFAULT_TOTAL_ITEM_FIELDS

// Batch Q: turns the wizard's plain {row, col, strip_before?, strip_after?}
// picks into the richer HeaderFieldSpec/ItemTableSpec shapes parseUsingTemplate
// understands, by reading the label text actually sitting near each picked
// cell in this sample -- deterministic (reads real cells), not an AI guess,
// so it can run entirely at save time regardless of how field_mappings was
// first proposed (AI parse or manual pick).
export function deriveAnchors(rows: any[][], fieldMappings: any): any {
  const out: any = {}

  for (const [field, spec] of Object.entries(fieldMappings || {})) {
    if (field === 'item_table') continue
    const specObj = spec as any
    if (!specObj || typeof specObj.row !== 'number' || typeof specObj.col !== 'number') {
      out[field] = specObj
      continue
    }

    if (DERIVE_FROM_ITEMS_FIELDS[field]) {
      // Staff explicitly picked a cell for a totals field (rare -- these
      // default to derive-from-items, see parseUsingTemplate) -- keep it as
      // an explicit override, same label heuristics as any other field.
      out[field] = deriveHeaderAnchor(rows, specObj)
      continue
    }

    out[field] = deriveHeaderAnchor(rows, specObj)
  }

  if (fieldMappings?.item_table) {
    out.item_table = deriveItemTableAnchors(rows, fieldMappings.item_table)
  }

  return out
}

// A trailing colon (incl. full-width "：") is dropped from the anchor text so
// a minor punctuation/spacing difference next month ("INVOICE NO. :" vs
// "INVOICE NO.:") doesn't stop the substring match from finding it.
function normalizeAnchorLabel(text: string): string {
  return text.replace(/[:：\s]+$/, '').trim()
}

// Locates the sheet cell whose (trimmed, case-insensitive) text exactly
// matches a reviewer's corrected value -- used when a template-replayed
// field's correction ISN'T a substring of the originally-mapped cell (i.e.
// the mapping points at the wrong cell entirely, not just a noisy label
// around the right one). Lets the template be re-anchored to the actual
// cell instead of replaying the same wrong one on every future upload.
// When more than one cell matches, prefers the one closest to nearRow
// (typically the field's previous row) to avoid jumping to an unrelated
// coincidental match elsewhere in the sheet.
export function findExactCell(rows: any[][], value: string, nearRow?: number): { row: number; col: number } | null {
  const target = value.trim().toLowerCase()
  if (!target) return null
  let best: { row: number; col: number } | null = null
  let bestDist = Infinity
  for (let r = 0; r < rows.length; r++) {
    const row = rows[r]
    if (!row) continue
    for (let c = 0; c < row.length; c++) {
      const cell = row[c]
      if (typeof cell !== 'string' && typeof cell !== 'number') continue
      if (String(cell).trim().toLowerCase() !== target) continue
      const dist = typeof nearRow === 'number' ? Math.abs(r - nearRow) : 0
      if (dist < bestDist) {
        bestDist = dist
        best = { row: r, col: c }
      }
    }
  }
  return best
}

export function deriveHeaderAnchor(rows: any[][], specObj: any): any {
  // Combined label+value cell (e.g. "INVOICE NO. : 2026-...") -- strip_before
  // IS the label, already known to be a prefix of this exact cell, so the
  // anchor and the value are the same cell (value_offset 0,0).
  if (specObj.strip_before) {
    const label = normalizeAnchorLabel(String(specObj.strip_before))
    if (label) {
      return { ...specObj, strategy: 'anchor', anchor_label: label, anchor_row_hint: specObj.row, value_offset: { row: 0, col: 0 } }
    }
  }

  // Separate label cell -- same heuristic computeStructureSignature already
  // uses: a short label sitting directly above the value.
  const above = String(rows[specObj.row - 1]?.[specObj.col] ?? '').trim()
  if (above && above.length <= 40) {
    return {
      ...specObj,
      strategy: 'anchor',
      anchor_label: normalizeAnchorLabel(above),
      anchor_row_hint: specObj.row - 1,
      value_offset: { row: 1, col: 0 },
    }
  }

  // No usable nearby label -- nothing to anchor on, stays a plain fixed pick.
  return { ...specObj, strategy: 'fixed' }
}

function deriveItemTableAnchors(rows: any[][], itemTable: any): any {
  const startRow = itemTable.start_row
  if (typeof startRow !== 'number') return itemTable
  const headerRow = startRow - 1
  const headerRowCells: any[] = rows[headerRow] || []

  const columns: Record<string, any> = {}
  const headerLabels: string[] = []
  for (const [field, colSpec] of Object.entries(itemTable.columns || {})) {
    const isObj = typeof colSpec === 'object' && colSpec !== null
    const col = isObj ? (colSpec as any).col : colSpec
    const label = typeof col === 'number' ? String(headerRowCells[col] ?? '').trim() : ''
    columns[field] = isObj ? { ...(colSpec as any), header_label: label || undefined } : (label ? { col, header_label: label } : col)
    if (label) headerLabels.push(label)
  }

  return {
    ...itemTable,
    header_row_hint: headerRow,
    header_labels: headerLabels,
    data_start_row_offset: itemTable.data_start_row_offset ?? 1,
    columns,
  }
}
